//src/components/pendent-load/PendentLoadWorkspace.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Info,
  Maximize2,
  Minimize2,
  Navigation,
  Pencil,
  Share2,
  Truck,
  X,
} from "lucide-react";

import {
  FaCheckCircle,
} from "react-icons/fa";

import {
  useTranslation,
} from "react-i18next";

import EditableShipmentRouteMap from "../maps/EditableShipmentRouteMap.jsx";

import PendentLoadRoutePanel from "./PendentLoadRoutePanel.jsx";
import PendentLoadDetailsPanel from "./PendentLoadDetailsPanel.jsx";
import PendentLoadAuditPanel from "./PendentLoadAuditPanel.jsx";

import {
  buildPendentLoadMapPoints,
  buildPendentLoadWorkspaceStops,
  getPendentLoadPointCoordinates,
  workspaceStopToPendentLoadPoint,
} from "../../utils/pendentLoadShipmentAdapter.js";

const API_BASE_URL =
  "/api";

function getDispatcherAuthToken() {
  return String(
    localStorage.getItem(
      "token"
    ) ||
      sessionStorage.getItem(
        "token"
      ) ||
      localStorage.getItem(
        "authToken"
      ) ||
      sessionStorage.getItem(
        "authToken"
      ) ||
      localStorage.getItem(
        "accessToken"
      ) ||
      ""
  )
    .trim()
    .replace(
      /^Bearer\s+/i,
      ""
    )
    .replace(
      /^"|"$/g,
      ""
    );
}

function formatDistance(
  value,
  locale,
  t
) {
  const numberValue =
    Number(
      value
    );

  if (
    !Number.isFinite(
      numberValue
    ) ||
    numberValue <=
      0
  ) {
    return t(
      "common.calculating"
    );
  }

  return t(
    "workspace.distanceValue",
    {
      value:
        numberValue.toLocaleString(
          locale,
          {
            maximumFractionDigits:
              numberValue >=
              100
                ? 0
                : 1,
          }
        ),
    }
  );
}

function formatDuration(
  value,
  t
) {
  const seconds =
    Number(
      value
    );

  if (
    !Number.isFinite(
      seconds
    ) ||
    seconds <=
      0
  ) {
    return t(
      "common.calculating"
    );
  }

  const totalMinutes =
    Math.round(
      seconds /
        60
    );

  const hours =
    Math.floor(
      totalMinutes /
        60
    );

  const minutes =
    totalMinutes %
    60;

  if (
    hours <=
    0
  ) {
    return t(
      "workspace.durationMinutes",
      {
        minutes,
      }
    );
  }

  if (
    minutes ===
    0
  ) {
    return t(
      "workspace.durationHours",
      {
        hours,
      }
    );
  }

  return t(
    "workspace.durationHoursMinutes",
    {
      hours,
      minutes,
    }
  );
}

function formatCurrency(
  value,
  locale,
  t
) {
  const numberValue =
    Number(
      value
    );

  if (
    !Number.isFinite(
      numberValue
    )
  ) {
    return t(
      "common.calculating"
    );
  }

  return new Intl.NumberFormat(
    locale,
    {
      style:
        "currency",

      currency:
        "EUR",
    }
  ).format(
    numberValue
  );
}

function getRouteTollAmount(
  routeResult
) {
  const candidates = [
    routeResult
      ?.tollEstimate
      ?.amount,

    routeResult
      ?.tollEstimate
      ?.total,

    routeResult
      ?.tollAmount,

    routeResult
      ?.tollsAmount,

    routeResult
      ?.tollCost,
  ];

  const amount =
    candidates
      .map(
        Number
      )
      .find(
        Number.isFinite
      );

  return amount ??
    null;
}

function formatLongDate(
  value,
  locale,
  t
) {
  const rawValue =
    String(
      value ||
        ""
    ).trim();

  if (
    !rawValue
  ) {
    return t(
      "workspace.deliveryTimeUndefined"
    );
  }

  const match =
    rawValue.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (
    !match
  ) {
    return rawValue;
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day:
        "2-digit",

      month:
        "long",

      year:
        "numeric",
    }
  ).format(
    new Date(
      Number(
        match[1]
      ),

      Number(
        match[2]
      ) -
        1,

      Number(
        match[3]
      )
    )
  );
}

function getPointName(
  point,
  fallback
) {
  return String(
    point?.location ||
      point?.placeName ||
      fallback
  ).trim();
}

function getPositiveRouteNumber(
  value,
  fallback
) {
  const numberValue =
    Number(
      value
    );

  return (
    Number.isFinite(
      numberValue
    ) &&
    numberValue >
      0
  )
    ? numberValue
    : fallback;
}

function getPositiveRouteInteger(
  value,
  fallback
) {
  const numberValue =
    Number(
      value
    );

  return (
    Number.isInteger(
      numberValue
    ) &&
    numberValue >
      0
  )
    ? numberValue
    : fallback;
}

export default function PendentLoadWorkspace({
  ready,
  loading,
  error,
  successPopupOpen,
  onCloseSuccessPopup,
  onBack,
  onSave,
  onSaveNote,
  id,
  isShipmentRequest = false,
  requestId,
  prefill,
  currentPendentLoad,
  pickupPoints,
  setPickupPoints,
  deliveryPoints,
  setDeliveryPoints,
  makeEmptyPoint,
  removePickUpPlace,
  removeDeliveryPlace,
  maxPoints,
  merchandise,
  setMerchandise,
  weight,
  setWeight,
  length,
  setLength,
  width,
  setWidth,
  height,
  setHeight,
  trailerType,
  setTrailerType,
  trailerTypeOther,
  setTrailerTypeOther,
  trailerTypeOptions,
  placeOptions = [],
  loadingAccessOptions = [],
  handlingEquipmentOptions = [],
  isSpecialLoad = false,
  price,
  setPrice,
  price4subcontracted,
  setPrice4subcontracted,
  showPrice4subcontracted,
  clientType,
  setClientType,
  selectedClient,
  setSelectedClient,
  clientOptions,
  transportCarrierId,
  setTransportCarrierId,
  carrierOptions,
  reference,
  setReference,
  notes4driver,
  setNotes4driver,
  merchandiseError,
  weightError,
  lengthError,
  widthError,
  heightError,
  priceError,
  price4subcontractedError,

  priceProposals = [],
  latestPriceProposal = null,
  priceNegotiationLoading = false,
  priceNegotiationSubmitting = false,
  priceNegotiationError = "",
  priceProposalAmount = "",
  setPriceProposalAmount,
  priceProposalNote = "",
  setPriceProposalNote,
  canSendPriceProposal = false,
  canAcceptPriceProposal = false,
  canRejectPriceProposal = false,
  canWithdrawPriceProposal = false,
  waitingForShipperPriceResponse = false,
  onSendPriceProposal,
  onAcceptPriceProposal,
  onRejectPriceProposal,
  onWithdrawPriceProposal,
}) {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_pendentload"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt";

  const mapSectionRef =
    useRef(
      null
    );

  const routeSnapshotRef =
    useRef(
      null
    );

  const [
    routeResult,
    setRouteResult,
  ] =
    useState(
      null
    );

  const [
    routeValidation,
    setRouteValidation,
  ] =
    useState({
      status:
        "IDLE",

      routeValid:
        false,

      allLocationsAccessible:
        false,

      locations:
        [],
    });

  const [
    routeMapError,
    setRouteMapError,
  ] =
    useState(
      ""
    );

  const [
    isMapFullscreen,
    setIsMapFullscreen,
  ] =
    useState(
      false
    );

  const [
    isEditingRoute,
    setIsEditingRoute,
  ] =
    useState(
      false
    );

  const [
    showVehicleRestrictions,
    setShowVehicleRestrictions,
  ] =
    useState(
      true
    );

  const requestRouteData =
    useMemo(
      () => ({
        ...(prefill ||
          {}),

        ...(currentPendentLoad ||
          {}),
      }),
      [
        currentPendentLoad,
        prefill,
      ]
    );

  const requestRouteViaPoints =
    useMemo(
      () =>
        isShipmentRequest &&
        Array.isArray(
          requestRouteData
            .routeViaPoints
        )
          ? requestRouteData
              .routeViaPoints
          : [],
      [
        isShipmentRequest,
        requestRouteData
          .routeViaPoints,
      ]
    );

  const routeAvoidTolls =
    isShipmentRequest
      ? Boolean(
          requestRouteData
            .routeAvoidTolls
        )
      : false;

  const routeVehicleProfile =
    useMemo(
      () => {
        if (
          isShipmentRequest
        ) {
          return {
            type:
              requestRouteData
                .routeVehicleType ||
              "articulated_truck",

            height:
              getPositiveRouteNumber(
                requestRouteData
                  .routeVehicleHeight,
                4
              ),

            width:
              getPositiveRouteNumber(
                requestRouteData
                  .routeVehicleWidth,
                2.55
              ),

            length:
              getPositiveRouteNumber(
                requestRouteData
                  .routeVehicleLength,
                16.5
              ),

            weight:
              getPositiveRouteNumber(
                requestRouteData
                  .routeVehicleWeight,
                40
              ),

            axleCount:
              getPositiveRouteInteger(
                requestRouteData
                  .routeVehicleAxleCount,
                5
              ),
          };
        }

        return {
          type:
            "articulated_truck",

          height:
            getPositiveRouteNumber(
              height,
              4
            ),

          width:
            getPositiveRouteNumber(
              width,
              2.55
            ),

          length:
            getPositiveRouteNumber(
              length,
              16.5
            ),

          weight:
            getPositiveRouteNumber(
              weight,
              40
            ),

          axleCount:
            5,
        };
      },
      [
        height,
        isShipmentRequest,
        length,
        requestRouteData
          .routeVehicleAxleCount,
        requestRouteData
          .routeVehicleHeight,
        requestRouteData
          .routeVehicleLength,
        requestRouteData
          .routeVehicleType,
        requestRouteData
          .routeVehicleWeight,
        requestRouteData
          .routeVehicleWidth,
        weight,
        width,
      ]
    );

  const persistedRouteResult =
    useMemo(
      () => {
        if (
          !isShipmentRequest
        ) {
          return null;
        }

        const distanceKm =
          Number(
            requestRouteData
              .routeDistanceKm
          );

        const durationSeconds =
          Number(
            requestRouteData
              .routeDurationSeconds
          );

        const tollAmount =
          Number(
            requestRouteData
              .routeTollAmount
          );

        const hasDistance =
          Number.isFinite(
            distanceKm
          ) &&
          distanceKm >
            0;

        const hasDuration =
          Number.isFinite(
            durationSeconds
          ) &&
          durationSeconds >
            0;

        const hasTollAmount =
          Number.isFinite(
            tollAmount
          );

        if (
          !hasDistance &&
          !hasDuration &&
          !requestRouteData
            .routePolyline
        ) {
          return null;
        }

        return {
          distanceKm:
            hasDistance
              ? distanceKm
              : null,

          durationSeconds:
            hasDuration
              ? durationSeconds
              : null,

          polyline:
            requestRouteData
              .routePolyline ||
            null,

          source:
            requestRouteData
              .routeSource ||
            "SAVED_REQUEST_ROUTE",

          hasTolls:
            Boolean(
              requestRouteData
                .routeHasTolls ||
              (
                hasTollAmount &&
                tollAmount >
                  0
              )
            ),

          tollAmount:
            hasTollAmount
              ? tollAmount
              : null,
        };
      },
      [
        isShipmentRequest,
        requestRouteData
          .routeDistanceKm,
        requestRouteData
          .routeDurationSeconds,
        requestRouteData
          .routeHasTolls,
        requestRouteData
          .routePolyline,
        requestRouteData
          .routeSource,
        requestRouteData
          .routeTollAmount,
      ]
    );

  const displayedRouteResult =
    routeResult ||
    persistedRouteResult;

  const workspaceStops =
    useMemo(
      () =>
        buildPendentLoadWorkspaceStops(
          pickupPoints,
          deliveryPoints,
          {
            preserveGlobalSequence:
              isShipmentRequest,
          }
        ),
      [
        deliveryPoints,
        isShipmentRequest,
        pickupPoints,
      ]
    );

  const routeMapPoints =
    useMemo(
      () =>
        buildPendentLoadMapPoints(
          workspaceStops,
          requestRouteViaPoints
        ),
      [
        requestRouteViaPoints,
        workspaceStops,
      ]
    );
  
  useEffect(
    () => {
      if (
        !isShipmentRequest
      ) {
        return;
      }

      /*
       * Quando chega um snapshot realtime novo,
       * deixa de mostrar o cálculo anterior enquanto
       * o mapa recalcula a rota com os dados novos.
       */
      setRouteResult(
        null
      );
    },
    [
      isShipmentRequest,
      requestRouteData
        .routePolyline,
      requestRouteData
        .routeUpdatedAt,
      requestRouteData
        .updatedAt,
    ]
  );

  const pointsWithoutCoordinates =
    useMemo(
      () =>
        workspaceStops.filter(
          (
            stop
          ) =>
            !getPendentLoadPointCoordinates(
              stop
            )
        ).length,
      [
        workspaceStops,
      ]
    );

  const firstPickupPoint =
    Array.isArray(
      pickupPoints
    ) &&
    pickupPoints.length >
      0
      ? pickupPoints[0]
      : null;

  const lastDeliveryPoint =
    Array.isArray(
      deliveryPoints
    ) &&
    deliveryPoints.length >
      0
      ? deliveryPoints[
          deliveryPoints.length -
            1
        ]
      : null;

  const pendentLoadNumber =
    currentPendentLoad
      ?.pendentLoadNumber ||
    prefill
      ?.pendentLoadNumber ||
    id;

  const firstPickupName =
    getPointName(
      firstPickupPoint,
      t(
        "workspace.origin"
      )
    );

  const lastDeliveryName =
    getPointName(
      lastDeliveryPoint,
      t(
        "workspace.destination"
      )
    );

  const finalDeliveryDate =
    formatLongDate(
      lastDeliveryPoint
        ?.date,
      locale,
      t
    );

  const finalDeliveryHour =
    String(
      lastDeliveryPoint
        ?.hour ||
        ""
    ).trim();

  const routeTollAmount =
    getRouteTollAmount(
      displayedRouteResult
    );

  const hasCalculatedRoute =
    Boolean(
      displayedRouteResult
        ?.geometry ||
      displayedRouteResult
        ?.polyline ||
      Number.isFinite(
        Number(
          displayedRouteResult
            ?.distanceKm
        )
      )
    );

  const routeHasTolls =
    Boolean(
      displayedRouteResult
        ?.hasTolls ||
      (
        routeTollAmount !==
          null &&
        routeTollAmount >
          0
      )
    );

  const selectedCarrier =
    useMemo(
      () =>
        (
          Array.isArray(
            carrierOptions
          )
            ? carrierOptions
            : []
        ).find(
          (
            option
          ) =>
            String(
              option.value
            ) ===
            String(
              transportCarrierId
                ?.value
            )
        ) ||
        transportCarrierId ||
        null,
      [
        carrierOptions,
        transportCarrierId,
      ]
    );

  useEffect(
    () => {
      const handleFullscreenChange =
        () => {
          setIsMapFullscreen(
            Boolean(
              mapSectionRef.current &&
              document.fullscreenElement ===
                mapSectionRef.current
            )
          );
        };

      document.addEventListener(
        "fullscreenchange",
        handleFullscreenChange
      );

      handleFullscreenChange();

      return () => {
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
      };
    },
    []
  );

  const buildUpdatedWorkspacePoints =
    (
      originalStop,
      updatedStop
    ) => {
      const nextPoint =
        workspaceStopToPendentLoadPoint(
          updatedStop
        );

      const mergePoint =
        (
          point
        ) => ({
          ...point,
          ...nextPoint,

          workingHours:
            nextPoint?.workingHours ??
            point?.workingHours ??
            null,
        });

      if (
        originalStop
          .__sourceType ===
        "PICKUP"
      ) {
        return {
          nextPickupPoints:
            pickupPoints.map(
              (
                point,
                index
              ) =>
                index ===
                originalStop
                  .__sourceIndex
                  ? mergePoint(
                      point
                    )
                  : point
            ),

          nextDeliveryPoints:
            deliveryPoints,
        };
      }

      return {
        nextPickupPoints:
          pickupPoints,

        nextDeliveryPoints:
          deliveryPoints.map(
            (
              point,
              index
            ) =>
              index ===
              originalStop
                .__sourceIndex
                ? mergePoint(
                    point
                  )
                : point
          ),
      };
    };

  const updateWorkspaceStop =
    (
      originalStop,
      updatedStop
    ) => {
      const {
        nextPickupPoints,
        nextDeliveryPoints,
      } =
        buildUpdatedWorkspacePoints(
          originalStop,
          updatedStop
        );

      setPickupPoints(
        nextPickupPoints
      );

      setDeliveryPoints(
        nextDeliveryPoints
      );
    };

  const saveWorkspaceStop =
    async (
      originalStop,
      updatedStop
    ) => {
      const {
        nextPickupPoints,
        nextDeliveryPoints,
      } =
        buildUpdatedWorkspacePoints(
          originalStop,
          updatedStop
        );

      const saved =
        await onSave?.(
          null,
          {
            pickupPoints:
              nextPickupPoints,

            deliveryPoints:
              nextDeliveryPoints,

            suppressSuccessPopup:
              true,
          }
        );

      return (
        saved !==
        false
      );
    };

  const removeWorkspaceStop =
    async (
      stop
    ) => {
      if (
        stop.__sourceType ===
        "PICKUP"
      ) {
        if (
          pickupPoints.length <=
          1
        ) {
          return false;
        }

        const nextPickupPoints =
          pickupPoints.filter(
            (
              _,
              index
            ) =>
              index !==
              stop.__sourceIndex
          );

        if (
          typeof onSave !==
          "function"
        ) {
          removePickUpPlace(
            stop.__sourceIndex
          );

          return true;
        }

        const saved =
          await onSave(
            null,
            {
              pickupPoints:
                nextPickupPoints,

              deliveryPoints,

              suppressSuccessPopup:
                true,
            }
          );

        return (
          saved !==
          false
        );
      }

      if (
        deliveryPoints.length <=
        1
      ) {
        return false;
      }

      const nextDeliveryPoints =
        deliveryPoints.filter(
          (
            _,
            index
          ) =>
            index !==
            stop.__sourceIndex
        );

      if (
        typeof onSave !==
        "function"
      ) {
        removeDeliveryPlace(
          stop.__sourceIndex
        );

        return true;
      }

      const saved =
        await onSave(
          null,
          {
            pickupPoints,

            deliveryPoints:
              nextDeliveryPoints,

            suppressSuccessPopup:
              true,
          }
        );

      return (
        saved !==
        false
      );
    };

  const addPickup =
    (
      newPoint
    ) => {
      if (
        pickupPoints.length >=
        maxPoints
      ) {
        return false;
      }

      setPickupPoints(
        (
          currentPoints
        ) => [
          ...currentPoints,

          {
            ...makeEmptyPoint(),
            ...(newPoint ||
              {}),
          },
        ]
      );

      return true;
    };

  const addDelivery =
    (
      newPoint
    ) => {
      if (
        deliveryPoints.length >=
        maxPoints
      ) {
        return false;
      }

      setDeliveryPoints(
        (
          currentPoints
        ) => [
          ...currentPoints,

          {
            ...makeEmptyPoint(),
            ...(newPoint ||
              {}),
          },
        ]
      );

      return true;
    };

  const moveRoutePoint =
    ({
      pointId,
      point,
      lat,
      lng,
      location,
    }) => {
      const targetStop =
        workspaceStops.find(
          (
            stop
          ) =>
            String(
              stop.__workspaceKey ||
                stop.id
            ) ===
            String(
              pointId
            )
        ) ||
        point ||
        null;

      const latitude =
        Number(
          lat
        );

      const longitude =
        Number(
          lng
        );

      if (
        !targetStop ||
        !Number.isFinite(
          latitude
        ) ||
        !Number.isFinite(
          longitude
        )
      ) {
        return;
      }

      const resolvedLocation =
        location ||
        {};

      updateWorkspaceStop(
        targetStop,
        {
          ...targetStop,

          latitude,

          longitude,

          coordinates:
            `${latitude.toFixed(
              7
            )}, ${longitude.toFixed(
              7
            )}`,

          location:
            resolvedLocation
              .name ||
            resolvedLocation
              .placeName ||
            resolvedLocation
              .displayName ||
            targetStop
              .location ||
            targetStop
              .placeName ||
            "",

          placeName:
            resolvedLocation
              .name ||
            resolvedLocation
              .placeName ||
            resolvedLocation
              .displayName ||
            targetStop
              .placeName ||
            targetStop
              .location ||
            "",

          address:
            resolvedLocation
              .address ||
            targetStop
              .address ||
            "",

          postalCode:
            resolvedLocation
              .postalCode ||
            resolvedLocation
              .postcode ||
            targetStop
              .postalCode ||
            "",

          city:
            resolvedLocation
              .city ||
            resolvedLocation
              .town ||
            resolvedLocation
              .village ||
            targetStop
              .city ||
            "",

          country:
            resolvedLocation
              .country ||
            targetStop
              .country ||
            "",
        }
      );
    };

  const startRouteEditing =
    () => {
      if (
        isShipmentRequest
      ) {
        return;
      }

      routeSnapshotRef.current = {
        pickupPoints:
          pickupPoints.map(
            (
              point
            ) => ({
              ...point,
            })
          ),

        deliveryPoints:
          deliveryPoints.map(
            (
              point
            ) => ({
              ...point,
            })
          ),
      };

      setRouteMapError(
        ""
      );

      setIsEditingRoute(
        true
      );
    };

  const cancelRouteEditing =
    () => {
      const snapshot =
        routeSnapshotRef.current;

      if (
        snapshot
      ) {
        setPickupPoints(
          snapshot.pickupPoints
        );

        setDeliveryPoints(
          snapshot.deliveryPoints
        );
      }

      routeSnapshotRef.current =
        null;

      setRouteMapError(
        ""
      );

      setIsEditingRoute(
        false
      );
    };

  const saveRouteEditing =
    async () => {
      const saved =
        await onSave?.();

      if (
        saved ===
        false
      ) {
        return;
      }

      setIsEditingRoute(
        false
      );

      routeSnapshotRef.current =
        null;
    };

  const expandMap =
    async () => {
      const mapSection =
        mapSectionRef.current;

      if (
        !mapSection
      ) {
        return;
      }

      try {
        if (
          document.fullscreenElement ===
          mapSection
        ) {
          await document
            .exitFullscreen?.();

          return;
        }

        await mapSection
          .requestFullscreen?.();
      } catch (
        fullscreenError
      ) {
        console.warn(
          "[PendentLoadWorkspace] Could not toggle map fullscreen:",
          fullscreenError
        );
      }
    };

  if (
    !ready
  ) {
    return (
      <div className="active-shipment-page-v2 edit-pendent-load-page-v2">
        <div className="active-shipment-shell-v2">
          <div className="edit-pendent-load-loading-state">
            <Truck
              size={
                18
              }
            />

            <span>
              {t(
                "workspace.loading"
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="active-shipment-page-v2 edit-pendent-load-page-v2">
      <div className="active-shipment-shell-v2">
        {successPopupOpen && (
          <div
            className="edit-pendent-load-popupOverlay"
            role="dialog"
            aria-modal="true"
          >
            <div className="edit-pendent-load-popupModal">
              <div className="edit-pendent-load-popupIconWrap">
                <FaCheckCircle className="edit-pendent-load-popupIcon" />
              </div>

              <h2 className="edit-pendent-load-popupTitle">
                {t(
                  "popup.updated_title"
                )}
              </h2>

              <p className="edit-pendent-load-popupMessage">
                {t(
                  "popup.updated_message"
                )}
              </p>

              <div className="edit-pendent-load-popupActions">
                <button
                  type="button"
                  className="edit-pendent-load-popupPrimaryBtn"
                  onClick={
                    onCloseSuccessPopup
                  }
                >
                  {t(
                    "popup.ok"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <header className="shipment-v2-header edit-pendent-load-header-v2">
          <div className="shipment-v2-header-left">
            <button
              type="button"
              className="shipment-v2-back-btn"
              onClick={
                onBack
              }
            >
              <ArrowLeft
                size={
                  15
                }
              />

              {t(
                "workspace.backToPendingLoads"
              )}
            </button>

            <div className="shipment-v2-title-row pendent-workspace-title-row">
              <h1>
                {firstPickupName}

                <span className="pendent-workspace-title-arrow">
                  →
                </span>

                {lastDeliveryName}

                <span className="pendent-workspace-title-reference">
                  {t(
                    "workspace.loadReference",
                    {
                      number:
                        String(
                          pendentLoadNumber
                        ).padStart(
                          5,
                          "0"
                        ),
                    }
                  )}
                </span>
              </h1>

              <span className="shipment-v2-divider" />

              <span className="shipment-v2-status-pill pendent-workspace-status-pill">
                <Truck
                  size={
                    13
                  }
                />

                {t(
                  "common.pending"
                )}
              </span>
            </div>

            <div className="shipment-v2-route-summary">
              <span>
                {firstPickupName}
              </span>

              <span>
                →
              </span>

              <span>
                {lastDeliveryName}
              </span>
            </div>
          </div>
        </header>

        <section
          ref={
            mapSectionRef
          }
          className={[
            "shipment-v2-map-section",
            "pendent-workspace-map-section",

            isMapFullscreen
              ? "is-map-fullscreen"
              : "",
          ]
            .filter(
              Boolean
            )
            .join(
              " "
            )}
        >
          <div className="edit-request-route-map-toolbar">
            <div className="edit-request-route-map-toolbar__main">
              <span
                className="edit-request-route-map-toolbar__route-icon"
                aria-hidden="true"
              >
                <Share2
                  size={
                    23
                  }
                />
              </span>

              <div className="edit-request-route-map-toolbar__distance">
                <strong>
                  {formatDistance(
                    displayedRouteResult
                      ?.distanceKm,
                    locale,
                    t
                  )}
                </strong>
              </div>

              <span
                className="edit-request-route-map-toolbar__divider"
                aria-hidden="true"
              />

              <div className="edit-request-route-map-toolbar__metric">
                <strong>
                  {formatDuration(
                    displayedRouteResult
                      ?.durationSeconds,
                    t
                  )}
                </strong>

                <small>
                  {t(
                    "workspace.estimatedDuration"
                  )}
                </small>
              </div>

              <span
                className="edit-request-route-map-toolbar__divider"
                aria-hidden="true"
              />

              <div className="edit-request-route-map-toolbar__metric edit-request-route-map-toolbar__metric--tolls">
                <strong>
                  {!hasCalculatedRoute
                    ? t(
                        "common.calculating"
                      )
                    : routeHasTolls
                      ? routeTollAmount !==
                        null
                        ? formatCurrency(
                            routeTollAmount,
                            locale,
                            t
                          )
                        : t(
                            "common.calculating"
                          )
                      : t(
                          "workspace.noTolls"
                        )}
                </strong>

                <small>
                  {!hasCalculatedRoute
                    ? t(
                        "workspace.calculatingTolls"
                      )
                    : routeHasTolls
                      ? t(
                          "workspace.estimatedTolls"
                        )
                      : t(
                          "workspace.routeWithoutTolls"
                        )}
                </small>
              </div>

              {hasCalculatedRoute &&
                routeHasTolls &&
                routeTollAmount !==
                  null && (
                  <span
                    className="edit-request-route-map-toolbar__information"
                    title={
                      t(
                        "workspace.tollsAmount",
                        {
                          amount:
                            formatCurrency(
                              routeTollAmount,
                              locale,
                              t
                            ),
                        }
                      )
                    }
                    aria-label={
                      t(
                        "workspace.tollsAmount",
                        {
                          amount:
                            formatCurrency(
                              routeTollAmount,
                              locale,
                              t
                            ),
                        }
                      )
                    }
                  >
                    <Info
                      size={
                        15
                      }
                    />
                  </span>
                )}
            </div>

            <div className="edit-request-route-map-toolbar__right">
              <button
                type="button"
                className="edit-request-vehicle-profile-button"
                title={
                  isShipmentRequest
                    ? t(
                        "workspace.vehicleProfile.shipperDefined"
                      )
                    : t(
                        "workspace.vehicleProfile.usesLoadDimensions"
                      )
                }
              >
                <span className="edit-request-vehicle-profile-button__icon">
                  <Truck
                    size={
                      19
                    }
                  />
                </span>

                <span className="edit-request-vehicle-profile-button__copy">
                  <strong>
                    {t(
                      "workspace.vehicleProfile.title"
                    )}
                  </strong>

                  <small>
                    {t(
                      "workspace.vehicleProfile.summary",
                      {
                        height:
                          routeVehicleProfile
                            .height
                            .toLocaleString(
                              locale,
                              {
                                minimumFractionDigits:
                                  2,

                                maximumFractionDigits:
                                  2,
                              }
                            ),

                        weight:
                          routeVehicleProfile
                            .weight
                            .toLocaleString(
                              locale,
                              {
                                minimumFractionDigits:
                                  2,

                                maximumFractionDigits:
                                  2,
                              }
                            ),
                      }
                    )}
                  </small>
                </span>

                <ChevronDown
                  className="edit-request-vehicle-profile-button__chevron"
                  size={
                    17
                  }
                />
              </button>
            </div>
          </div>

          <div className="shipment-v2-eta-card">
            <div className="shipment-v2-eta-card__header">
              <span className="shipment-v2-eta-card__status-icon">
                <Check
                  size={
                    14
                  }
                />
              </span>

              <strong>
                {t(
                  "workspace.finalDeliveryExpected"
                )}
              </strong>
            </div>

            <div className="shipment-v2-eta-card__date-panel">
              <span className="shipment-v2-eta-card__date-icon">
                <CalendarDays
                  size={
                    18
                  }
                />
              </span>

              <div className="shipment-v2-eta-card__date-copy">
                <span>
                  {t(
                    "workspace.date"
                  )}
                </span>

                <strong>
                  {finalDeliveryDate}
                </strong>

                {finalDeliveryHour && (
                  <small>
                    {finalDeliveryHour}
                  </small>
                )}
              </div>
            </div>

            <div className="shipment-v2-eta-meta">
              <Clock3
                size={
                  13
                }
              />

              <span>
                {t(
                  "workspace.configuredStops",
                  {
                    count:
                      workspaceStops.length,
                  }
                )}
              </span>
            </div>

            <button
              type="button"
              onClick={() =>
                document
                  .querySelector(
                    ".pendent-workspace-route-panel"
                  )
                  ?.scrollIntoView({
                    behavior:
                      "smooth",

                    block:
                      "start",
                  })
              }
            >
              <span className="shipment-v2-eta-card__button-content">
                <Navigation
                  size={
                    14
                  }
                />

                <strong>
                  {t(
                    "workspace.viewFullRoute"
                  )}
                </strong>
              </span>

              <ChevronRight
                size={
                  15
                }
              />
            </button>
          </div>

          <EditableShipmentRouteMap
            points={
              routeMapPoints
            }
            editable={
              !isShipmentRequest &&
              isEditingRoute
            }
            showSearch={
              isMapFullscreen
            }
            avoidTolls={
              routeAvoidTolls
            }
            apiBaseUrl={
              API_BASE_URL
            }
            authToken={
              getDispatcherAuthToken()
            }
            showVehicleRestrictions={
              !isShipmentRequest &&
              showVehicleRestrictions
            }
            truck={{
              height:
                routeVehicleProfile
                  .height,

              width:
                routeVehicleProfile
                  .width,

              length:
                routeVehicleProfile
                  .length,

              weight:
                routeVehicleProfile
                  .weight,

              axleCount:
                routeVehicleProfile
                  .axleCount,
            }}
            onPointMoved={
              isShipmentRequest
                ? undefined
                : moveRoutePoint
            }
            onRouteChange={
              setRouteResult
            }
            onRouteValidationChange={(
              nextValidation
            ) =>
              setRouteValidation(
                nextValidation || {
                  status:
                    "IDLE",

                  routeValid:
                    false,

                  allLocationsAccessible:
                    false,

                  locations:
                    [],
                }
              )
            }
            onError={(
              nextError
            ) =>
              setRouteMapError(
                nextError
                  ? t(
                      "errors.routeMapFailed"
                    )
                  : ""
              )
            }
          />

          <div className="shipment-v2-map-actions">
            {isEditingRoute ? (
              <>
                <button
                  type="button"
                  className="edit-request-map-action-cancel"
                  disabled={
                    loading
                  }
                  onClick={
                    cancelRouteEditing
                  }
                >
                  <span className="shipment-v2-map-actions__content">
                    <X
                      size={
                        19
                      }
                    />

                    <span>
                      <strong>
                        {t(
                          "workspace.map.cancelEditing"
                        )}
                      </strong>

                      <small>
                        {t(
                          "workspace.map.restorePreviousRoute"
                        )}
                      </small>
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  className="edit-request-map-action-save"
                  disabled={
                    loading ||
                    routeValidation
                      .status ===
                      "INVALID"
                  }
                  onClick={
                    saveRouteEditing
                  }
                >
                  <span className="shipment-v2-map-actions__content">
                    <CheckCircle2
                      size={
                        19
                      }
                    />

                    <span>
                      <strong>
                        {t(
                          "workspace.map.saveRoute"
                        )}
                      </strong>

                      <small>
                        {t(
                          "workspace.map.applyChanges"
                        )}
                      </small>
                    </span>
                  </span>
                </button>
              </>
            ) : (
              !isShipmentRequest && (
                <button
                  type="button"
                  className="edit-request-map-action-edit"
                  onClick={
                    startRouteEditing
                  }
                >
                  <span className="shipment-v2-map-actions__content">
                    <Pencil
                      size={
                        19
                      }
                    />

                    <span>
                      <strong>
                        {t(
                          "workspace.map.editRoute"
                        )}
                      </strong>
                    </span>
                  </span>

                  <ChevronRight
                    size={
                      20
                    }
                  />
                </button>
              )
            )}

            <button
              type="button"
              onClick={
                expandMap
              }
            >
              <span className="shipment-v2-map-actions__content">
                {isMapFullscreen ? (
                  <Minimize2
                    size={
                      19
                    }
                  />
                ) : (
                  <Maximize2
                    size={
                      19
                    }
                  />
                )}

                <span>
                  <strong>
                    {isMapFullscreen
                      ? t(
                          "workspace.map.collapse"
                        )
                      : t(
                          "workspace.map.expand"
                        )}
                  </strong>
                </span>
              </span>

              <ChevronRight
                size={
                  20
                }
              />
            </button>
          </div>

          {isEditingRoute && (
            <label className="pendent-workspace-restrictions-toggle">
              <input
                type="checkbox"
                checked={
                  showVehicleRestrictions
                }
                onChange={(event) =>
                  setShowVehicleRestrictions(
                    event.target
                      .checked
                  )
                }
              />

              {t(
                "workspace.map.showVehicleRestrictions"
              )}
            </label>
          )}
        </section>

        {routeMapError && (
          <div className="edit-pendent-load-map-feedback is-error">
            <Info
              size={
                14
              }
            />

            {routeMapError}
          </div>
        )}

        {pointsWithoutCoordinates >
          0 && (
          <div className="edit-pendent-load-map-feedback is-warning">
            <Info
              size={
                14
              }
            />

            {t(
              "workspace.map.pointsWithoutCoordinates",
              {
                count:
                  pointsWithoutCoordinates,
              }
            )}
          </div>
        )}

        <main className="shipment-v2-main-grid pendent-workspace-main-grid">
          <PendentLoadRoutePanel
            stops={
              workspaceStops
            }
            readOnly={
              isShipmentRequest
            }
            onUpdateStop={
              updateWorkspaceStop
            }
            onSaveStop={
              saveWorkspaceStop
            }
            onRemoveStop={
              removeWorkspaceStop
            }
            onAddPickup={
              addPickup
            }
            onAddDelivery={
              addDelivery
            }
            canAddPickup={
              pickupPoints.length <
              maxPoints
            }
            canAddDelivery={
              deliveryPoints.length <
              maxPoints
            }
            placeOptions={
              placeOptions
            }
            loadingAccessOptions={
              loadingAccessOptions
            }
            handlingEquipmentOptions={
              handlingEquipmentOptions
            }
            isSpecialLoad={
              isSpecialLoad
            }
          />

          <PendentLoadDetailsPanel
            loading={
              loading
            }
            isShipmentRequest={
              isShipmentRequest
            }
            onSave={
              onSave
            }
            pendentLoadNumber={
              pendentLoadNumber
            }
            currentPendentLoad={
              currentPendentLoad
            }
            merchandise={
              merchandise
            }
            setMerchandise={
              setMerchandise
            }
            weight={
              weight
            }
            setWeight={
              setWeight
            }
            length={
              length
            }
            setLength={
              setLength
            }
            width={
              width
            }
            setWidth={
              setWidth
            }
            height={
              height
            }
            setHeight={
              setHeight
            }
            trailerType={
              trailerType
            }
            setTrailerType={
              setTrailerType
            }
            trailerTypeOther={
              trailerTypeOther
            }
            setTrailerTypeOther={
              setTrailerTypeOther
            }
            trailerTypeOptions={
              trailerTypeOptions
            }
            price={
              price
            }
            setPrice={
              setPrice
            }
            price4subcontracted={
              price4subcontracted
            }
            setPrice4subcontracted={
              setPrice4subcontracted
            }
            showPrice4subcontracted={
              showPrice4subcontracted
            }
            clientType={
              clientType
            }
            setClientType={
              setClientType
            }
            selectedClient={
              selectedClient
            }
            setSelectedClient={
              setSelectedClient
            }
            clientOptions={
              clientOptions
            }
            transportCarrierId={
              transportCarrierId
            }
            setTransportCarrierId={
              setTransportCarrierId
            }
            carrierOptions={
              carrierOptions
            }
            reference={
              reference
            }
            setReference={
              setReference
            }
            notes4driver={
              notes4driver
            }
            setNotes4driver={
              setNotes4driver
            }
            merchandiseError={
              merchandiseError
            }
            weightError={
              weightError
            }
            lengthError={
              lengthError
            }
            widthError={
              widthError
            }
            heightError={
              heightError
            }
            priceError={
              priceError
            }
            price4subcontractedError={
              price4subcontractedError
            }

            priceProposals={
              priceProposals
            }
            latestPriceProposal={
              latestPriceProposal
            }
            priceNegotiationLoading={
              priceNegotiationLoading
            }
            priceNegotiationSubmitting={
              priceNegotiationSubmitting
            }
            priceNegotiationError={
              priceNegotiationError
            }
            priceProposalAmount={
              priceProposalAmount
            }
            setPriceProposalAmount={
              setPriceProposalAmount
            }
            priceProposalNote={
              priceProposalNote
            }
            setPriceProposalNote={
              setPriceProposalNote
            }
            canSendPriceProposal={
              canSendPriceProposal
            }
            canAcceptPriceProposal={
              canAcceptPriceProposal
            }
            canRejectPriceProposal={
              canRejectPriceProposal
            }
            canWithdrawPriceProposal={
              canWithdrawPriceProposal
            }
            waitingForShipperPriceResponse={
              waitingForShipperPriceResponse
            }
            onSendPriceProposal={
              onSendPriceProposal
            }
            onAcceptPriceProposal={
              onAcceptPriceProposal
            }
            onRejectPriceProposal={
              onRejectPriceProposal
            }
            onWithdrawPriceProposal={
              onWithdrawPriceProposal
            }
          />

          <PendentLoadAuditPanel
            loading={
              loading
            }
            requestId={
              requestId
            }
            pendentLoadNumber={
              pendentLoadNumber
            }
            pickupCount={
              pickupPoints.length
            }
            deliveryCount={
              deliveryPoints.length
            }
            merchandise={
              merchandise
            }
            notes4driver={
              notes4driver
            }
            setNotes4driver={
              setNotes4driver
            }
            selectedCarrier={
              selectedCarrier
            }
            currentPendentLoad={
              currentPendentLoad
            }
            onSaveNote={
              onSaveNote
            }
          />
        </main>

        {error && (
          <div className="edit-pendent-load-page-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}